import { backend, backendOld } from "~/api";
import { getShownPropertyWithConfigList } from "~/common/properties";
import { copyAndNotify } from "~/components/notifications";
import { convertTaskToMarkdown } from "~/components/text/transformers";
import { EventKind, RelationshipKindKind, TaskSourceKind, ViewMode } from "~/shared/enums";
import type { Task } from "~/shared/types";
import { useDataStore, useEnvironmentStore, useUserStore } from "~/stores";
import { getAgentTaskLink, getBuildTaskLink, makeDuid, slugifyStr, trimSlugStr } from "~/utils/common";
import { getTitleOrNoTitle } from "~/utils/entityTitle";
import { getOrdersBetween } from "~/utils/orderManager";

import type { Actions } from "..";

const LIST_ITEM_REGEX = /^\s*((([-*+](\s\[.\])?)|\d+[.)/]{1,2})\s+)(?<content>.*?)\s*$/;

/** Other non-field task actions */
export default (actions: Actions) => ({
  /**
   * Create tasks from text. Returns the last task text or undefined if there is none.
   * @param {string} text - text to create tasks from
   * @param {string} dartboardDuid - DUID of dartboard to create tasks in
   * @param {string} aboveTaskOrder - order of the task above the group to create
   * @param {string} belowTaskOrder - order of the task below the group to create
   * @param {object} options - options
   * @returns {string} - remaining text
   */
  createTasksFromText: (
    text: string,
    dartboardDuid: string,
    aboveTaskOrder: string,
    belowTaskOrder: string,
    partialTask?: Partial<Task>,
    options: {
      parentTaskDuid?: string;
      createAll?: boolean;
    } = {}
  ): string | undefined => {
    const { parentTaskDuid, createAll = false } = options;

    const dataStore = useDataStore();

    const parentKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
    const shouldCreateSubtasks = !!parentTaskDuid;

    const allTaskTexts = text
      .split("\n")
      .map((e) => LIST_ITEM_REGEX.exec(e)?.groups?.content ?? e)
      .filter(Boolean);
    const remainingText = createAll ? undefined : allTaskTexts.shift();

    const orders = getOrdersBetween(aboveTaskOrder, belowTaskOrder, allTaskTexts.length);
    const titlePrefix = partialTask?.title ? `${partialTask.title} ` : "";
    const taskPartials = allTaskTexts.map((title, i) => ({
      duid: makeDuid(),
      statusDuid: dataStore.defaultDefaultUnstartedStatus.duid,
      dartboardDuid: dartboardDuid ?? "",
      order: orders[i],
      relationships: shouldCreateSubtasks
        ? [
            {
              duid: makeDuid(),
              kindDuid: parentKindDuid,
              targetDuid: parentTaskDuid,
              isForward: false,
            },
          ]
        : [],
      ...partialTask,
      title: titlePrefix + title,
    }));

    if (taskPartials.length > 0) {
      dataStore.createTasks(taskPartials, TaskSourceKind.APP_PASTE);
    }

    return remainingText;
  },

  /**
   * Copy the links to tasks to the clipboard
   * @param {Task[]} tasks
   */
  copyLinks: (tasks: Task[]): void => {
    const dataStore = useDataStore();
    const environmentStore = useEnvironmentStore();
    const userStore = useUserStore();

    if (tasks.length === 0) {
      return;
    }

    const getTaskUrl = (task: Task) => {
      if (userStore.viewMode === ViewMode.AGENTS) {
        return `${environmentStore.urlBase}${dataStore.$router.resolve(getAgentTaskLink(task)).href}`;
      }
      if (userStore.viewMode === ViewMode.BUILD) {
        return `${environmentStore.urlBase}${dataStore.$router.resolve(getBuildTaskLink(task)).href}`;
      }
      return environmentStore.getTaskUrl(task);
    };

    const content: string[][] = tasks
      .map((e) => [e.title, getTaskUrl(e)])
      .map(([title, url]) => [url, `<a href=${url}>${getTitleOrNoTitle(title)}</a>`]);

    const plural = content.length > 1 ? "s" : "";
    copyAndNotify(
      `Link${plural} to task${plural}`,
      content.map((e) => e[0]).join("\n"),
      content.map((e) => e[1]).join("<br />")
    );
    backend.event.create(EventKind.USAGE_COPY_BRANCH, { count: tasks.length });
  },
  /**
   * Copy the branch names for the tasks to the clipboard
   * @param {Task[]} tasks
   */
  copyBranchNames: (tasks: Task[]): void => {
    const userStore = useUserStore();

    const branchNames = tasks.map((task) => {
      // TODO Make this call in bulk
      backendOld.vcs.onBranchLinkCopy(task.duid);
      // TODO allow different calculation methods
      // TODO improve GH username calculation
      const username = slugifyStr(userStore.email.split("@")[0], { lower: true });
      const title = slugifyStr(task.title, { lower: true });
      return trimSlugStr(`${username}/${task.duid}-${title}`, { len: 60 });
    });

    copyAndNotify(`Branch name${branchNames.length > 1 ? "s" : ""}`, branchNames.join("\n"));
    backend.event.create(EventKind.USAGE_COPY_BRANCH);
  },
  /**
   * Copy the prompts for the tasks to the clipboard
   * @param {Task[]} tasks
   */
  copyAsPrompt: async (tasks: Task[]): Promise<void> => {
    const environmentStore = useEnvironmentStore();
    const dataStore = useDataStore();

    const singular = tasks.length === 1;
    const parts = await Promise.all(
      tasks.map(async (task) => {
        const markdown = await convertTaskToMarkdown(task, getShownPropertyWithConfigList, dataStore, true);
        const url = environmentStore.getTaskUrl(task);
        return `[Task ${task.duid}](${url})\n\n${markdown}`;
      })
    );
    const content = `Work on ${singular ? "this" : "these"} task${singular ? "" : "s"} from Dart\n\n---\n\n${parts.join("\n\n---\n\n")}`;

    copyAndNotify(`Task prompt${tasks.length > 1 ? "s" : ""}`, content);
  },
});
